import React from 'react';
import { useSelector } from 'react-redux';
import { getUserSettings } from '@surfline/web-common';

import CamPlayerKbygRewindsWrapper from 'components/CamPlayerKbygRewinds/CamPlayerKbygRewindsWrapper';
import RewindsAndInsightsCamsAndAnglesContainer from 'components/RewindsAndInsightsCamsAndAnglesContainer';
import { RewindsContextProvider } from 'contexts/RewindsContext';
import useCams from 'hooks/useCams';
import useCameraURLManagement from 'hooks/useCameraURLManagement';
import { getSpotReportAssociated } from 'selectors/spot';
import { useUserPermissionStatus, useIsInternalUser } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { Camera } from 'types/camera';
import type { SpotDetails } from 'types/spot';

import styles from './SpotKbygRewinds.module.scss';

interface EnabledFeatures {
  ads: {
    page: boolean;
    video: boolean;
  };
  paywalls: boolean;
  wavePool: boolean;
}

interface Props {
  cameras: Camera[];
  enabledFeatures?: EnabledFeatures;
  spot: SpotDetails;
}

const SpotKbygRewinds: React.FC<Props> = ({
  cameras,
  enabledFeatures = { ads: { page: true, video: true }, paywalls: true, wavePool: false },
  spot,
}) => {
  const spotReportAssociated = useAppSelector(getSpotReportAssociated);
  const timezone = spotReportAssociated?.timezone ?? 'America/Los_Angeles';
  const userSettings = useSelector(getUserSettings);
  const surfHeightUnit = userSettings?.units.surfHeight;
  const { hasLineupCamPermissions } = useUserPermissionStatus();
  const includeInternalCams = useIsInternalUser();

  const {
    multiCamViewActive,
    setMultiCamViewActive,
    primaryCam,
    setPrimaryCamId,
    setActiveMenu,
    sortedCameras,
    numCamsDisplayed,
    multiCamLoaded,
  } = useCams(cameras, spot._id, true, hasLineupCamPermissions, includeInternalCams);

  useCameraURLManagement({
    cameras,
    isMultiCam: multiCamViewActive,
    isWavePool: enabledFeatures.wavePool,
    multiCamLoaded,
    primaryCamId: primaryCam?._id,
    spotId: spot._id,
    spotName: spot.name,
  });

  return (
    <RewindsContextProvider
      surfHeightUnit={surfHeightUnit}
      selectedCamera={primaryCam}
      timezone={timezone}
    >
      <RewindsAndInsightsCamsAndAnglesContainer
        cameras={cameras}
        disablePageAds={enabledFeatures.ads.page === false}
        isSpotPage
        multiCamViewActive={multiCamViewActive}
        selectedCamera={primaryCam}
        setPrimaryCamId={setPrimaryCamId}
        spot={spot}
      >
        <div className={styles.spotKbygRewinds} data-testid="kbyg-rewinds">
          {primaryCam && (
            <CamPlayerKbygRewindsWrapper
              camera={primaryCam}
              enabledFeatures={enabledFeatures}
              sortedCameras={sortedCameras}
              setMultiCamViewActive={setMultiCamViewActive}
              multiCamViewActive={multiCamViewActive}
              setActiveMenu={setActiveMenu}
              isMultiCam={cameras.length > 1}
              numCamsDisplayed={numCamsDisplayed}
            />
          )}
        </div>
      </RewindsAndInsightsCamsAndAnglesContainer>
    </RewindsContextProvider>
  );
};

export default SpotKbygRewinds;
